import { useState, useEffect } from 'react';
import { css } from 'styled-jsx/css';
import classnames from 'classnames';
import isValid from 'date-fns/isValid';
import parseISO from 'date-fns/parseISO';
import FormControl, { Label, Error } from '../elements/FormControl';
import { colors, fonts } from '../Theme';

const dateStyles = css`
  .date {
    display: inline-flex;
    border: 2px solid ${colors.ywgVeryLightBlue};
    margin-bottom: 20px;
  }
  @media (max-width: 800px) {
    .date {
      justify-content: space-between;
    }
  }
  .date:focus-within {
    outline: solid 2px ${colors.ywgPinkishOrange};
  }
  .date__input {
    text-align: center;
    border: none;
    box-sizing: border-box;
    color: black;
    font-family: ${fonts.secondaryFont};
    font-size: 18px;
    padding: 12px 20px 12px 20px;
    appearance: none;
    background: white url('/static/images/arrow-bottom.svg') no-repeat 97% 50%;
    flex-grow: 1;
    max-width: 100px;
  }

  .date__input--large {
    max-width: 200px;
  }
  .date__placeholder {
    font-style: italic;
  }
`;

const parseOrNull = value => {
  const parsed = parseISO(value + 'T00:00:00Z');
  return isValid(parsed) ? parsed : null;
};

const numberOptions = (
  minValue = 1,
  maxValue,
  placeholder = '',
  reverse = false
) => {
  const emptyOption = (
    <option disabled key="">
      {placeholder}
    </option>
  );
  const options = [];
  for (let index = minValue; index <= maxValue; index++) {
    options.push(
      <option value={index} key={index}>
        {index}
      </option>
    );
  }
  if (reverse) {
    return [emptyOption].concat(options.reverse());
  } else {
    return [emptyOption].concat(options);
  }
};

const SelectDateControl = ({
  value,
  onChange,
  onBlur,
  name,
  required,
  label,
  valid,
  error,
  className,
  mode = 'birthday',
  dayPlaceholder = 'jour',
  monthPlaceholder = 'mois',
  yearPlaceholder = 'année'
}) => {
  const date = parseOrNull(value);
  const currentYear = new Date().getFullYear();
  const minYear = mode === 'travel' ? currentYear : currentYear - 66;
  const maxYear = mode === 'travel' ? currentYear + 2 : currentYear;

  const [day, setDay] = useState(date ? date.getUTCDate() : null);
  const [month, setMonth] = useState(date ? date.getUTCMonth() + 1 : null);
  const [year, setYear] = useState(date ? date.getFullYear() : null);

  const onDateChange = (day = day, month = month, year = year) => {
    if (
      day &&
      day !== dayPlaceholder &&
      month &&
      month !== monthPlaceholder &&
      year &&
      year !== yearPlaceholder
    ) {
      const updatedDate = new Date(Date.UTC(year, month - 1, day));
      onChange({
        target: { value: updatedDate.toISOString().split('T')[0], name: name }
      });
    } else {
      onBlur({
        target: { value: null, name: name }
      });
    }
  };
  const onDateBlur = (day = day, month = month, year = year) => {
    if (
      day &&
      day !== dayPlaceholder &&
      month &&
      month !== monthPlaceholder &&
      year &&
      year !== yearPlaceholder
    ) {
      const updatedDate = new Date(Date.UTC(year, month - 1, day));
      onBlur({
        target: { value: updatedDate.toISOString().split('T')[0], name: name }
      });
    } else {
      onBlur({
        target: { value: null, name: name }
      });
    }
  };

  const onDayChange = evt => {
    setDay(evt.target.value);
    onDateChange(evt.target.value, month, year);
  };

  const onMonthChange = evt => {
    setMonth(evt.target.value);
    onDateChange(day, evt.target.value, year);
  };

  const onYearChange = evt => {
    setYear(evt.target.value);
    onDateChange(day, month, evt.target.value);
  };

  const onDayBlur = evt => {
    setDay(evt.target.value);
    onDateBlur(evt.target.value, month, year);
  };

  const onMonthBlur = evt => {
    setMonth(evt.target.value);
    onDateBlur(day, evt.target.value, year);
  };

  const onYearBlur = evt => {
    setYear(evt.target.value);
    onDateBlur(day, month, evt.target.value);
  };

  return (
    <>
      <Label htmlFor={name + '-day'} required={required}>
        {label}
      </Label>
      <div className={classnames('date', className)}>
        <select
          className={classnames('date__input', 'day', className, {
            date__placeholder: !day
          })}
          onChange={onDayChange}
          onBlur={onDayBlur}
          name={name + '-day'}
          value={day || dayPlaceholder}
          required={required}
          id={name + '-day'}
        >
          {numberOptions(1, 31, dayPlaceholder)}
        </select>
        <select
          className={classnames('date__input', 'month', className, {
            date__placeholder: !month
          })}
          onChange={onMonthChange}
          onBlur={onMonthBlur}
          name={name + '-month'}
          id={name + '-month'}
          value={month || monthPlaceholder}
          required={required}
        >
          {numberOptions(1, 12, monthPlaceholder)}
        </select>
        <select
          className={classnames(
            'date__input',
            'date__input--large',
            'year',
            className,
            {
              date__placeholder: !year
            }
          )}
          onChange={onYearChange}
          onBlur={onYearBlur}
          name={name + '-year'}
          id={name + '-year'}
          value={year || yearPlaceholder}
          required={required}
        >
          {numberOptions(minYear, maxYear, yearPlaceholder, true)}
        </select>
      </div>
      <Error valid={valid} error={error} />
      <style jsx>{dateStyles}</style>
    </>
  );
};

const hasHtml5DateInput = () => {
  if (typeof document !== 'undefined') {
    const input = document.createElement('input');
    const value = 'a';
    input.setAttribute('type', 'date');
    input.setAttribute('value', value);
    return input.value !== value;
  } else {
    return false;
  }
};
const DateControl = ({
  value,
  onChange,
  onBlur,
  name,
  required,
  label,
  valid,
  error,
  className,
  mode = 'birthday',
  dayPlaceholder = 'jour',
  monthPlaceholder = 'mois',
  yearPlaceholder = 'année'
}) => {
  const [useHtml5, setUseHtml5] = useState(true);
  useEffect(() => {
    setUseHtml5(hasHtml5DateInput());
  }, []);
  if (useHtml5) {
    return (
      <FormControl
        type="date"
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        name={name}
        required={required}
        label={label}
        valid={valid}
        error={error}
        className={className}
      />
    );
  } else {
    return (
      <SelectDateControl
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        name={name}
        required={required}
        label={label}
        valid={valid}
        error={error}
        className={className}
        mode={mode}
        dayPlaceholder={dayPlaceholder}
        monthPlaceholder={monthPlaceholder}
        yearPlaceholder={yearPlaceholder}
      />
    );
  }
};
export default DateControl;
